import React from "react";
import { scrollPageToTop } from "../utils/paginationScroll";

export default function ProductsPaginationBar({
  page,
  totalPages,
  totalProducts = 0,
  isLoading = false,
  onPageChange,
  className = "",
}) {
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  if (!totalProducts && page <= 1 && !hasNext && !isLoading) return null;

  const goToPage = (targetPage) => {
    if (targetPage < 1 || targetPage > totalPages || isLoading) return;
    onPageChange?.(targetPage);
    scrollPageToTop();
  };

  const formattedTotal = Number(totalProducts).toLocaleString();

  return (
    <div className={`mt-6 flex w-full justify-center ${className}`.trim()}>
      <div
        className="flex w-full max-w-full flex-col items-center gap-2 rounded-2xl border border-[#E6EAF0] bg-white px-3 py-2.5 shadow-sm sm:inline-flex sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-3 sm:rounded-full sm:px-3 sm:py-1.5"
        role="navigation"
        aria-label="Results pages"
      >
        <div className="inline-flex max-w-full items-stretch overflow-hidden rounded-full border border-[#E6EAF0] bg-[#F8FAFC]">
          <button
            type="button"
            disabled={!hasPrev || isLoading}
            onClick={() => goToPage(Math.max(1, page - 1))}
            className="inline-flex h-9 min-w-[2.75rem] items-center justify-center gap-1 px-2.5 text-[13px] font-semibold text-[#6E7B86] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40 sm:h-8 sm:min-w-0 sm:px-3"
            aria-label="Previous page"
          >
            <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="hidden sm:inline">Prev</span>
          </button>

          <span className="flex min-w-[4.5rem] items-center justify-center border-x border-[#E6EAF0] bg-white px-3 text-[13px] font-semibold tabular-nums sm:min-w-[3.5rem]">
            <span className="text-[#7352C7]">{page}</span>
            <span className="mx-0.5 text-[#C5CDD6]">/</span>
            <span className="text-[#24303A]">{totalPages}</span>
          </span>

          <button
            type="button"
            disabled={!hasNext || isLoading}
            onClick={() => goToPage(page + 1)}
            className="inline-flex h-9 min-w-[2.75rem] items-center justify-center gap-1 bg-[#7352C7] px-2.5 text-[13px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-40 sm:h-8 sm:min-w-0 sm:px-3"
            aria-label="Next page"
          >
            <span className="hidden sm:inline">Next</span>
            <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M7.5 5L12.5 10L7.5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <span className="hidden h-3 w-px shrink-0 bg-[#E6EAF0] sm:block" aria-hidden="true" />

        <span className="text-center text-[12px] text-[#6E7B86] sm:text-[13px]">
          <span className="sm:hidden">
            <span className="font-semibold text-[#24303A]">{formattedTotal}</span> products
          </span>
          <span className="hidden sm:inline">
            Total products:{" "}
            <span className="font-semibold text-[#24303A]">{formattedTotal}</span>
          </span>
        </span>
      </div>
    </div>
  );
}
